// PadSplit Sync tab (Smart Locks page) — compares the codes PadSplit tracks
// per listing (harvested via the warm-browser fleet) against the codes on the
// physical locks, per property. Staff can confirm lock mappings, add a
// missing code to the physical lock, rotate a room's code, and push codes to
// PadSplit through the warm-browser fleet. Visuals per the approved mockup.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage, parseErrorStatus } from "@/lib/queryClient";
import { sortProperties } from "@/lib/property-utils";
import type { Property } from "@shared/schema";
import type {
  LockCodeComparison,
  LockCodeComparisonRow,
  LockComparisonGroup,
} from "./padsplit-sync-types";
import { PadsplitLockCompareCard } from "./padsplit-lock-compare-table";
import { CreateLockCodeDialog, type CreateCodeTarget } from "./create-lock-code-dialog";
import { usePadsplitLockProgress } from "./use-padsplit-lock-progress";

function formatSyncAge(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, { credentials: "include", signal });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { message?: string }).message || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export function PadsplitSyncTab({ readOnly }: { readOnly: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [createTarget, setCreateTarget] = useState<CreateCodeTarget | null>(null);
  const [addToLockCodeId, setAddToLockCodeId] = useState<number | null>(null);
  const [retryPushId, setRetryPushId] = useState<number | null>(null);
  const identityQuery = useQuery<{ user?: { id: number }; activeMembership?: { ownerUserId: number } | null }>({
    queryKey: ["/api/app/auth/me"],
  });
  const sessionKey = `${identityQuery.data?.user?.id ?? ""}:${identityQuery.data?.activeMembership?.ownerUserId ?? ""}`;

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });
  const padsplitProperties = useMemo(
    () => sortProperties((propertiesQuery.data ?? []).filter((p) => p.padsplitPropertyId)),
    [propertiesQuery.data],
  );

  useEffect(() => {
    if (!padsplitProperties.some((property) => property.id === selectedPropertyId)) {
      setSelectedPropertyId(padsplitProperties[0]?.id ?? null);
    }
  }, [selectedPropertyId, padsplitProperties]);

  const comparisonKey = ["/api/app/devices/padsplit-locks/comparison", selectedPropertyId] as const;
  const comparisonQuery = useQuery<LockCodeComparison>({
    queryKey: comparisonKey,
    queryFn: ({ signal }) =>
      fetchJson<LockCodeComparison>(`/api/app/devices/padsplit-locks/comparison?propertyId=${selectedPropertyId}`, signal),
    enabled: selectedPropertyId != null,
    // Never poll providers. The database-only observer invalidates this exact
    // property when persisted lock timestamps or push state actually changes.
    refetchOnWindowFocus: false,
  });
  const comparison = comparisonQuery.data;
  const progress = usePadsplitLockProgress(selectedPropertyId, sessionKey, comparison);
  const harvestActive = ["preparing", "awaiting", "partial"].includes(progress.harvestPhase);

  useEffect(() => {
    setCreateTarget(null);
    setAddToLockCodeId(null);
    setRetryPushId(null);
  }, [selectedPropertyId, sessionKey]);

  const deviceSettingsQuery = useQuery<{ codeDigits?: number }>({
    queryKey: ["/api/app/devices/settings"],
  });
  const codeDigits = deviceSettingsQuery.data?.codeDigits ?? 4;

  const harvestMutation = useMutation({
    mutationFn: () => progress.harvest(),
    onSuccess: (accepted) => {
      if (!accepted) return;
      toast({
        title: "PadSplit sync accepted",
        description: "Awaiting a persisted refresh. Acceptance does not confirm that lock data has changed.",
      });
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't start the sync",
        description: parseErrorStatus(err) === 409
          ? "A harvest is already running. Try again later; a fresh snapshot is not yet confirmed."
          : parseErrorStatus(err) === 429
            ? "PadSplit is throttling requests. Wait before trying again."
            : parseErrorMessage(err, "Failed to request the PadSplit lock sync."),
        variant: "destructive",
      });
    },
  });

  const mappingMutation = useMutation({
    mutationFn: async (opts: { padsplitLockKey: string; deviceId: string }) =>
      apiRequest("POST", "/api/app/devices/padsplit-locks/mappings", {
        propertyId: selectedPropertyId,
        padsplitLockKey: opts.padsplitLockKey,
        deviceId: opts.deviceId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: comparisonKey, exact: true });
      toast({ title: "Lock mapped" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't save the mapping",
        description: parseErrorMessage(err, "Failed to save the lock mapping."),
        variant: "destructive",
      });
    },
  });

  // "Add to lock" — the code exists in PadSplit but not on the physical lock:
  // program the PadSplit code onto the mapped device.
  const addToLockMutation = useMutation({
    mutationFn: async (opts: { group: LockComparisonGroup; row: LockCodeComparisonRow }) => {
      const mapping = opts.group.mapping;
      if (!mapping) throw new Error("Map this lock to a physical lock first.");
      const digits = String(opts.row.padsplitCode ?? "").replace(/[^0-9]/g, "");
      if (!digits) throw new Error("This PadSplit code has no digits to program.");
      const label = `${opts.row.roomLabel ?? "Room"}${opts.row.assignedTo ? ` - ${opts.row.assignedTo}` : ""}`;
      if (mapping.provider === "seam") {
        await apiRequest("POST", "/api/app/devices/seam/access-codes", {
          connectionId: mapping.connectionId,
          deviceId: mapping.deviceId,
          name: label,
          code: digits,
        });
      } else {
        const parts = String(mapping.deviceId ?? "").split("-");
        await apiRequest("POST", "/api/app/devices/ttlock/access-codes", {
          connectionId: mapping.connectionId,
          lockId: Number(parts[parts.length - 1]),
          keyboardPwd: digits,
          keyboardPwdName: label,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: comparisonKey, exact: true });
      toast({ title: "Code added to the lock" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't add the code",
        description: parseErrorMessage(err, "Failed to program the code on the lock."),
        variant: "destructive",
      });
    },
    onSettled: () => setAddToLockCodeId(null),
  });

  const retryMutation = useMutation({
    mutationFn: async (opts: { pushId: number; propertyId: number | null; sessionKey: string }) =>
      progress.retryPush(opts.pushId),
    onSuccess: (accepted, opts) => {
      if (!accepted || opts.propertyId !== selectedPropertyId || opts.sessionKey !== sessionKey) return;
      toast({ title: "Push retry accepted", description: "Awaiting delivery confirmation from the database." });
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't retry",
        description: parseErrorMessage(err, "Failed to retry the PadSplit push."),
        variant: "destructive",
      });
    },
    onSettled: () => setRetryPushId(null),
  });

  // A room's rotation spans every PadSplit lock carrying that room's row
  // (its own room lock + the common lock) — collect the siblings.
  const openNewCodeDialog = (group: LockComparisonGroup, row: LockCodeComparisonRow) => {
    const siblings = (comparison?.locks ?? []).filter(
      (g) =>
        g.padsplitLockKey !== group.padsplitLockKey &&
        g.rows.some((r) => r.roomLabel === row.roomLabel),
    );
    setCreateTarget({ group, row, siblingGroups: siblings });
  };

  const summary = comparison?.summary;

  return (
    <div data-testid="padsplit-sync-tab">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Select
            value={selectedPropertyId != null ? String(selectedPropertyId) : ""}
            onValueChange={(v) => setSelectedPropertyId(Number(v))}
          >
            <SelectTrigger className="w-[280px]" data-testid="select-padsplit-sync-property">
              <span className="flex items-center gap-2 truncate">
                <Home className="h-4 w-4 shrink-0 text-muted-foreground" />
                <SelectValue placeholder="Choose a property…" />
              </span>
            </SelectTrigger>
            <SelectContent>
              {padsplitProperties.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {comparison && (
            <span className="text-sm text-muted-foreground" data-testid="text-last-synced">
              Last synced from PadSplit:{" "}
              <strong className="text-foreground">{formatSyncAge(comparison.syncedAt)}</strong>
            </span>
          )}
        </div>
        {!readOnly && (
          <Button
            variant="outline"
            onClick={() => harvestMutation.mutate()}
            disabled={harvestActive || selectedPropertyId == null}
            data-testid="button-sync-from-padsplit"
          >
            {harvestActive ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1.5" />
            )}
            Sync from PadSplit
          </Button>
        )}
      </div>

      {progress.harvestPhase !== "idle" && (
        <div className="rounded-lg border bg-card p-3 mb-4 text-sm" role="status" data-testid="padsplit-harvest-progress">
          {progress.harvestPhase === "preparing" && "Reading a fresh property snapshot and requesting a harvest…"}
          {progress.harvestPhase === "awaiting" && "Request accepted — awaiting a persisted refresh. No existing lock timestamps have advanced yet; refresh is unconfirmed."}
          {progress.harvestPhase === "partial" && `Partial refresh: ${progress.refreshed} of ${progress.total} existing locks have newer timestamps. Still awaiting the remaining locks.`}
          {progress.harvestPhase === "confirmed" && `Persisted refresh observed for all ${progress.total} existing locks. This does not by itself confirm push delivery.`}
          {progress.harvestPhase === "timeout" && `Stopped checking after two minutes. ${progress.refreshed > 0 ? `Partial refresh: ${progress.refreshed} of ${progress.total} existing locks changed.` : "Refresh remains unconfirmed; no existing lock timestamps advanced."} Empty or new-only snapshots cannot confirm completion. You can request another harvest later.`}
          {progress.harvestPhase === "error" && "Harvest progress could not be confirmed. Check the error before trying again."}
        </div>
      )}
      {(progress.pendingPushes > 0 || progress.deliveredPushes > 0 || progress.failedPushes > 0 || progress.cancelledPushes > 0 || progress.pushTimedOut) && (
        <div className="rounded-lg border bg-card p-3 mb-4 text-sm space-y-2" role="status" data-testid="padsplit-push-progress">
          {progress.pendingPushes > 0 && <p>{progress.pendingPushes} push(es) awaiting delivery confirmation. Checking persisted status for up to two minutes.</p>}
          {progress.failedPushes > 0 && <p>Recorded property history: {progress.failedPushes} push(es) with delivery errors. An earlier error may remain visible while a retry awaits processing. Review the failed rows before retrying.</p>}
          {progress.cancelledPushes > 0 && <p>Recorded property history: {progress.cancelledPushes} cancelled push(es); not confirmed as delivered.</p>}
          {progress.deliveredPushes > 0 && <p>Recorded property history: {progress.deliveredPushes} delivered push(es), not necessarily the latest request. Delivery is separate from refreshing harvested lock data. If refresh is partial or unconfirmed, use “Sync from PadSplit” to explicitly request a later harvest. No harvest runs automatically.</p>}
          {progress.pushTimedOut && <p>Delivery checking timed out; unresolved pushes may still complete later.</p>}
          {progress.pushTimedOut && <Button variant="outline" size="sm" onClick={progress.checkPushes} data-testid="button-check-push-delivery">Check delivery again</Button>}
        </div>
      )}
      {progress.error && <p className="text-sm text-destructive mb-4" role="alert" data-testid="padsplit-progress-error">{progress.error}</p>}

      {/* Summary chips */}
      {summary && (
        <div className="flex gap-2 flex-wrap mb-5" data-testid="padsplit-sync-summary">
          <span className="inline-flex items-center gap-2 bg-card border rounded-full px-3.5 py-1.5 text-sm">
            <span className="w-2 h-2 rounded-full bg-green-500" /> {summary.inSync} in sync
          </span>
          <span className="inline-flex items-center gap-2 bg-card border rounded-full px-3.5 py-1.5 text-sm">
            <span className="w-2 h-2 rounded-full bg-amber-500" /> {summary.missingOnLock} missing on lock
          </span>
          <span className="inline-flex items-center gap-2 bg-card border rounded-full px-3.5 py-1.5 text-sm">
            <span className="w-2 h-2 rounded-full bg-gray-400" /> {summary.unmappedLocks} unmapped{" "}
            {summary.unmappedLocks === 1 ? "lock" : "locks"}
          </span>
          {summary.failedPushes > 0 && (
            <span className="inline-flex items-center gap-2 bg-card border border-red-200 rounded-full px-3.5 py-1.5 text-sm text-red-600">
              <span className="w-2 h-2 rounded-full bg-red-500" /> {summary.failedPushes} failed{" "}
              {summary.failedPushes === 1 ? "push" : "pushes"}
            </span>
          )}
        </div>
      )}

      {/* Body states */}
      {padsplitProperties.length === 0 && !propertiesQuery.isLoading && (
        <div className="text-center py-12 text-muted-foreground" data-testid="text-no-padsplit-properties">
          No PadSplit-linked properties found. Link a property to its PadSplit listing first.
        </div>
      )}
      {comparisonQuery.isLoading && selectedPropertyId != null && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {comparisonQuery.isError && (
        <div className="text-center py-12 text-muted-foreground" data-testid="text-comparison-error">
          {parseErrorMessage(comparisonQuery.error, "Failed to load the PadSplit code comparison.")}
        </div>
      )}
      {comparison && comparison.locks.length === 0 && (
        <div className="text-center py-12 text-muted-foreground" data-testid="text-no-padsplit-locks">
          No locks harvested from PadSplit for this listing yet. Click "Sync from PadSplit", then check
          back in a few minutes.
        </div>
      )}

      {/* Lock cards */}
      <div className="space-y-4">
        {(comparison?.locks ?? []).map((group) => (
          <PadsplitLockCompareCard
            key={group.padsplitLockKey}
            group={group}
            physicalLocks={comparison?.physicalLocks ?? []}
            onConfirmMapping={(g, deviceId) =>
              !readOnly && mappingMutation.mutate({ padsplitLockKey: g.padsplitLockKey, deviceId })
            }
            mappingPending={mappingMutation.isPending}
            onNewCode={(g, row) => !readOnly && openNewCodeDialog(g, row)}
            onAddToLock={(g, row) => {
              if (readOnly) return;
              setAddToLockCodeId(row.padsplitCodeId);
              addToLockMutation.mutate({ group: g, row });
            }}
            addToLockPendingCodeId={addToLockCodeId}
            onRetryPush={(pushId) => {
              if (readOnly) return;
              setRetryPushId(pushId);
              retryMutation.mutate({ pushId, propertyId: selectedPropertyId, sessionKey });
            }}
            retryPendingPushId={retryPushId}
          />
        ))}
      </div>

      {selectedPropertyId != null && (
        <CreateLockCodeDialog
          target={createTarget}
          onClose={() => setCreateTarget(null)}
          propertyId={selectedPropertyId}
          codeDigits={codeDigits}
          invalidateKeys={[[...comparisonKey]]}
        />
      )}
    </div>
  );
}
